import React, { useEffect, useState } from "react";
import PublicLayout from "../components/public/PublicLayout";
import AuthModal from "../components/AuthModal";
import { listPublicLeagues } from "../store/publicLeagues";
import { navigate } from "../utils/router";
import type { League } from "../store/leagues";

export default function LeaguesPublicPage() {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      const data = await listPublicLeagues();
      if (mounted) {
        setLeagues(data);
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  return (
    <PublicLayout activePath="/leagues" showBottomNav>
      <div className="max-w-6xl mx-auto px-4 pt-6 pb-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-wide">
              Cricket Leagues
            </h1>
            <p className="text-slate-400/80 text-sm mt-1">
              View tournaments, standings, and fixtures
            </p>
          </div>
          <button
            onClick={() => setShowAuthModal(true)}
            className="hidden sm:flex items-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create League
          </button>
        </div>

        {/* League Cards */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="lp-card rounded-2xl p-5 animate-pulse">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="h-5 w-40 bg-white/5 rounded" />
                    <div className="h-4 w-28 bg-white/5 rounded" />
                  </div>
                  <div className="h-6 w-16 bg-white/5 rounded-full" />
                </div>
                <div className="mt-4 flex gap-3">
                  <div className="h-8 w-20 bg-white/5 rounded-lg" />
                  <div className="h-8 w-20 bg-white/5 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : leagues.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {leagues.map((league) => (
              <LeagueCard key={league.id} league={league} />
            ))}
          </div>
        ) : (
          <div className="lp-card rounded-2xl p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No Public Leagues Yet</h2>
            <p className="text-slate-400/70 text-sm mb-6 max-w-sm mx-auto">
              Be the first to create a cricket league. Organise your local tournament and share it with the community.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all"
            >
              Login to Create a League
            </button>
          </div>
        )}
      </div>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      <style>{`
        .lp-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.2s ease;
        }
        .lp-card:hover {
          border-color: rgba(16, 185, 129, 0.2);
          background: rgba(255, 255, 255, 0.06);
        }
      `}</style>
    </PublicLayout>
  );
}

function LeagueCard({ league }: { league: League }) {
  const formatDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const isActive = () => {
    if (!league.end_date) return true;
    return new Date(league.end_date) >= new Date();
  };

  return (
    <div
      onClick={() => navigate(`/leagues/${league.id}`)}
      className="lp-card rounded-2xl p-5 cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">{league.name}</h3>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-slate-400 text-sm">{league.format}</span>
            {league.overs && (
              <span className="text-slate-400/70 text-sm">{league.overs} overs</span>
            )}
            {league.location && (
              <span className="text-slate-400/70 text-sm flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {league.location}
              </span>
            )}
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
          isActive()
            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
            : "bg-slate-600/20 text-slate-400 border border-slate-500/30"
        }`}>
          {isActive() ? "Active" : "Completed"}
        </span>
      </div>

      {(league.start_date || league.competition_type) && (
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {league.start_date && (
            <span className="text-slate-400/60 text-xs">
              {formatDate(league.start_date)}
              {league.end_date && ` - ${formatDate(league.end_date)}`}
            </span>
          )}
          {league.competition_type && (
            <span className="text-slate-400/60 text-xs bg-white/[0.04] px-2 py-0.5 rounded">
              {league.competition_type}
            </span>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-emerald-400/80 text-sm font-medium">
          View League
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}
